<template>
	<div id="super-bundle-view" v-cloak>
		<div class="page-shell">
			<div class="top-grid">
				<section class="hero-card">
					<div class="hero-title-row">
						<h1 class="hero-title">{{oSuperBundle.name}}</h1>
						<a class="hero-edit-link" v-if="isAdmin" :href="adminEditUrl" rel="nofollow">Edit</a>
					</div>

					<div class="hero-body">
						<div class="hero-preview-shell" v-if="showHeroPreviewSlider">
							<div class="hero-preview-slider">
								<button
									type="button"
									class="hero-preview-arrow hero-preview-arrow--left"
									@click="slideHeroPreviewPrev"
									:disabled="!canSlideHeroPreviewPrev"
									aria-label="Show previous preview images"
								>
									&lt;
								</button>

								<div class="hero-preview-viewport" ref="refHeroPreviewViewport">
									<div class="hero-preview-track" :style="heroPreviewTrackStyle">
										<div class="hero-preview-page" v-for="(page, pageIndex) in heroPreviewPages" :key="`hero-preview-page-${pageIndex}`" :style="heroPreviewPageStyle">
											<div class="hero-preview-card" v-for="image in page" :key="image.key" :class="{'hero-preview-card--placeholder': image.isPlaceholder}">
												<template v-if="image.isPlaceholder">
													<div class="hero-preview-placeholder">
														<span class="hero-preview-placeholder-mark">ContentBASE</span>
														<span class="hero-preview-placeholder-copy">More bundle previews</span>
													</div>
												</template>
												<img
													v-else
													:src="image.url"
													:alt="image.alt"
													:width="image.width"
													:height="image.height"
													:srcset="image.srcSet"
													:sizes="heroPreviewImageSizes"
													loading="lazy"
													decoding="async"
												>
											</div>
										</div>
									</div>
								</div>

								<button
									type="button"
									class="hero-preview-arrow hero-preview-arrow--right"
									@click="slideHeroPreviewNext"
									:disabled="!canSlideHeroPreviewNext"
									aria-label="Show next preview images"
								>
									&gt;
								</button>
							</div>
						</div>

						<div class="hero-description-shell" :class="{'hero-description-shell--after-preview': showHeroPreviewSlider}" v-if="showDescription">
							<div class="article-html hero-description" v-html="oSuperBundle.descriptionHtml"></div>
						</div>
					</div>
				</section>

				<aside class="purchase-column">
					<div class="purchase-card">
						<div class="ribbon-wrap a-right purchase-ribbon" v-show="ownsEntireSuperBundle">
							<div class="ribbon-content"><span>Owned!</span></div>
						</div>

						<div class="purchase-top-actions">
							<div class="purchase-button-container" v-show="showButtonGuest">
								<input type="button" class="btn btn-primary shadow-none purchase-button" :value="purchaseButtonText" @click="guestClicksPurchase">
							</div>
							<div class="purchase-button-container" v-show="showButtonClaim">
								<input type="button" class="btn btn-primary shadow-none purchase-button" :value="purchaseButtonText" @click="spendAccountCreditOnSuperBundle">
							</div>
							<div class="purchase-button-container" v-show="showButtonBuy">
								<input type="button" class="btn btn-primary shadow-none purchase-button" :value="purchaseButtonText" data-bs-toggle="modal" data-bs-target="#modal-container-payment-form" @click="preparePaymentForm">
							</div>
							<div class="purchase-button-container" v-show="showButtonProcessing">
								<input type="button" class="btn btn-primary shadow-none purchase-button" value="Processing..." disabled>
							</div>
						</div>

						<div class="purchase-login-box" v-if="showMessageLoginRegister">
							<p class="mb-0"><b>Login</b> or <b>register</b> to get started.</p>
							<button-facebook-login :app-id="fbAppId" :version="fbVersion" :width="loginButtonWidth" class="mt-2" v-if="showButtonFacebook"></button-facebook-login>
							<button-google-login :clientId="gClientId" :width="loginButtonWidth" :auto-prompt="googleAutoPrompt" class="mt-2" v-if="showButtonGoogle"></button-google-login>
							<button-contentbase-login :width="loginButtonWidth" class="mt-2"></button-contentbase-login>
						</div>

						<div class="purchase-price-block" v-if="isPaid">
							<p class="purchase-price-raw" v-if="showRawPrice">${{formattedCreditsUndiscounted}}</p>
							<p class="purchase-savings-row" v-if="showSavings">You save ${{formattedSavings}}!</p>
						</div>

						<div class="purchase-offer-specs" v-if="showPurchaseOfferSpecs">
							<p class="purchase-offer-line">{{purchaseInventoryText}}</p>
							<p class="purchase-offer-line purchase-offer-line--secondary" v-if="showPlatformBreakdown">{{platformBreakdownTextResolved}}</p>
						</div>

						<p class="purchase-summary-copy" v-if="showPurchaseSummary">{{purchaseSummaryText}}</p>

						<div class="purchase-granted-pill" v-if="showGrantedBundlesPill">
							<p class="purchase-granted-title">{{purchaseGrantedBundlesTitle}}</p>
							<ul class="purchase-granted-list">
								<li v-for="bundle in missingIncludedBundles" :key="`granted-${bundle.id}`">
									<a :href="bundleDetailUrl(bundle)" target="_blank" rel="noopener noreferrer">{{bundle.name}}</a>
								</li>
							</ul>
						</div>

						<div class="purchase-owned-box" v-if="showMessageSuperBundlePurchasedOrOwned">
							<img src="/img/packages/confetti-animated-64.gif" ref="refImgSuperBundlePurchased">
							<p class="purchase-owned-title" v-if="showMessageSuperBundlePurchased">Super bundle purchased!</p>
							<p class="purchase-owned-title" v-else>You already own every included bundle in this super bundle.</p>
							<p class="purchase-owned-copy" v-if="showMessageSuperBundlePurchased">
								All newly granted bundles are now available through
								<a href="/my-bundles/"><span class="fat-link">My Bundles</span></a>.
							</p>
							<p class="purchase-owned-copy" v-else>
								Your bundles are already available through
								<a href="/my-bundles/"><span class="fat-link">My Bundles</span></a>.
							</p>
						</div>
					</div>
				</aside>
			</div>

			<section class="content-card included-section">
				<div class="included-section-head">
					<h2 class="section-title">Included Bundles</h2>
				</div>

				<div class="bundle-slider-shell" v-if="includedBundles.length > 0">
					<button
						type="button"
						class="bundle-slider-arrow bundle-slider-arrow--left"
						@click="slideBundlesPrev"
						:disabled="!canSlideBundlesPrev"
						aria-label="Show previous bundles"
					>
						&lt;
					</button>

					<div class="bundle-slider-viewport" ref="refBundleSliderViewport">
						<div class="bundle-slider-track" :style="bundleSliderTrackStyle">
							<article class="bundle-entry-card" v-for="bundle in includedBundles" :key="bundle.id" :style="bundleSlideStyle">
								<div class="ribbon-wrap a-right included-ribbon" v-show="isIncludedBundleOwned(bundle)">
									<div class="ribbon-content"><span>Owned!</span></div>
								</div>

								<div class="bundle-entry-header">
									<h3 class="bundle-entry-title">
										<a :href="bundleDetailUrl(bundle)" target="_blank" rel="noopener noreferrer">{{bundle.name}}</a>
									</h3>
								</div>

								<a class="bundle-entry-media" :href="bundleDetailUrl(bundle)" target="_blank" rel="noopener noreferrer">
									<div class="bundle-collage" :class="bundleCollageClass(bundle)">
										<template v-if="bundleVisibleImages(bundle).length > 0">
											<div
												class="bundle-collage-tile"
												:class="bundleCollageTileClass(index)"
												v-for="(image, index) in bundleVisibleImages(bundle)"
												:key="`${bundle.id}-${index}`"
											>
												<img
													:src="image.url"
													:alt="bundle.name"
													:width="image.width"
													:height="image.height"
													loading="lazy"
												>

												<div class="bundle-collage-overlay" v-if="showBundleCollageOverflow(bundle, index)">
													+{{bundleCollageOverflowCount(bundle)}} more
												</div>
											</div>
										</template>

										<div class="bundle-collage-fallback" v-else>No preview available</div>
									</div>
								</a>

								<a
									class="bundle-entry-action-link"
									:href="bundleContextUrl(bundle)"
									:target="bundleContextTarget(bundle)"
									:rel="bundleContextRel(bundle)"
								>{{bundleContextLinkText(bundle)}}</a>
							</article>
						</div>
					</div>

					<button
						type="button"
						class="bundle-slider-arrow bundle-slider-arrow--right"
						@click="slideBundlesNext"
						:disabled="!canSlideBundlesNext"
						aria-label="Show next bundles"
					>
						&gt;
					</button>
				</div>

				<div class="included-empty" v-else>
					<p>No bundles are currently included in this super bundle.</p>
				</div>
			</section>
		</div>

		<div id="modal-container-sign-in-form" class="modal fade" tabindex="-1" role="dialog">
			<div class="modal-dialog modal-sm modal-dialog-centered" role="document">
				<div class="modal-content">
					<div class="modal-body">
						<form-sign-in show-login="false" :width="signInFormWidth" v-if="isNotLoggedIn && signInFormRequested"></form-sign-in>
						<p v-else-if="isLoggedIn">Sign in form unavailable. Please contact support.</p>
					</div>
				</div>
			</div>
		</div>

		<div id="modal-container-payment-form" class="modal fade" tabindex="-1" role="dialog">
			<div class="modal-dialog modal-dialog-centered modal-lg" role="document">
				<div class="modal-content">
					<div class="modal-body">
						<payment-form
							max-width="800"
							v-on:payment-started="paymentStarted($event)"
							v-on:payment-stopped="paymentStopped($event)"
							v-on:payment-completed="paymentCompleted($event)"
							mode="superBundle"
							:super-bundle-id="oSuperBundle.id"
							:super-bundle-name="oSuperBundle.name"
							:super-bundle-nr-credits="oSuperBundle.nrCredits"
							:usr-nr-credits="userNrCredits"
							v-if="showPaymentForm"
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>




<script>
	import pulseLoader from '@/img/loaders/Pulse-1s-200px.optimized.webp?inline';
	import { defineAsyncComponent, h } from 'vue';
	import Modal from 'bootstrap/js/dist/modal';
	import { mixinAxios } from '@/js/shared/VueMixins.js';
	import { ensurePartySdk } from '@/js/shared/utils/sdkLoader';
	import buttonFacebookLogin from './common/FacebookLoginButton.vue';
	import buttonGoogleLogin from './common/GoogleLoginButton.vue';
	import buttonContentbaseLogin from './common/ContentBaseLoginButton.vue';
	const formSignIn = defineAsyncComponent({
		loader: () => import('./common/SignInForm.vue'),
		loadingComponent: { render: () => h('p', { class: 'text-center py-4 cb-loading-status', role: 'status' }, 'Loading sign-in form...') },
		errorComponent: { render: () => h('p', { class: 'text-center py-4', role: 'alert' }, 'Could not load the sign-in form. Please refresh and try again.') },
		onError(error, retry, fail, attempts) { attempts < 2 ? retry() : fail(); },
		delay: 0
	});
	import { isNotNullOrWhiteSpace, toNrDecimals } from '@/js/shared/utils';

	const paymentFormLoader = {
		render() {
			return h('div', { class: 'text-center py-4' }, [
				h('img', {
					src: pulseLoader,
					alt: 'Loading payment options',
					class: 'img-payment-form-loader'
				})
			]);
		}
	};

	const paymentForm = defineAsyncComponent({
		loader: () => import('./common/PaymentForm.vue'),
		loadingComponent: paymentFormLoader,
		delay: 0
	});

	export default {
		mixins: [mixinAxios],
		components: {
			buttonFacebookLogin,
			buttonGoogleLogin,
			buttonContentbaseLogin,
			formSignIn,
			paymentForm
		},
		data: () => ({
			oSuperBundle: {},
			oPurchaseState: {},
			oVisitorStatus: {},
			showButtonFacebook: false,
			showButtonGoogle: false,
			fbAppId: '',
			fbVersion: '',
			gClientId: '',
			loginButtonWidth: '100%',
			signInFormWidth: 250,
			guestClickedPurchase: false,
			paymentFormRequested: false,
			signInFormRequested: false,
			directPurchaseSuccess: false,
			userNrCredits: 0,
			oCreditSpend: {},
			windowWidth: 1440,
			heroPreviewPageIndex: 0,
			heroPreviewViewportWidth: 0,
			heroPreviewResizeObserver: null,
			bundleSliderStartIndex: 0,
			bundleSliderViewportWidth: 0,
			bundleSliderResizeObserver: null
		}),
		props: {
			superBundleViewInfo: { type: Object, required: true, default: () => ({}) }
		},
		created() {
			this.initData();
		},
		mounted() {
			this.creditsUpdatedHandler = event => { this.userNrCredits = event.detail.nrCredits; };
			window.addEventListener('contentbase:credits-updated', this.creditsUpdatedHandler);
			this.signInModal = document.getElementById('modal-container-sign-in-form');
			this.signInModal?.addEventListener('show.bs.modal', this.prepareSignInForm);
			if (typeof window !== 'undefined') {
				this.windowWidth = window.innerWidth;
				window.addEventListener('resize', this.handleWindowResize);
			}

			this.$nextTick(() => {
				this.measureHeroPreviewSlider();
				this.measureBundleSlider();
				this.setupHeroPreviewObserver();
				this.setupBundleSliderObserver();
			});
		},
		beforeUnmount() {
			window.removeEventListener('contentbase:credits-updated', this.creditsUpdatedHandler);
			this.signInModal?.removeEventListener('show.bs.modal', this.prepareSignInForm);
			if (typeof window !== 'undefined') {
				window.removeEventListener('resize', this.handleWindowResize);
			}

			this.teardownHeroPreviewObserver();
			this.teardownBundleSliderObserver();
		},
		methods: {
			initData() {
				var info = this.superBundleViewInfo ?? {};
				this.oSuperBundle = info.superBundle ?? {};
				this.oPurchaseState = info.purchaseState ?? {};
				this.oVisitorStatus = info.visitorStatus ?? {};
				this.showButtonFacebook = info.showButtonFacebook ?? false;
				this.showButtonGoogle = info.showButtonGoogle ?? false;
				this.fbAppId = info.fbAppId ?? '';
				this.fbVersion = info.fbVersion ?? '';
				this.gClientId = info.gClientId ?? '';
				this.userNrCredits = this.oVisitorStatus?.nrCredits ?? 0;
				this.heroPreviewPageIndex = 0;
				this.bundleSliderStartIndex = 0;

				this.$nextTick(() => {
					this.measureHeroPreviewSlider();
					this.clampHeroPreviewPageIndex();
					this.measureBundleSlider();
					this.clampBundleSliderStartIndex();
				});
			},
			formatCredits(nrCredits) {
				return toNrDecimals(nrCredits ?? 0, 2, 2);
			},
			handleWindowResize() {
				this.windowWidth = window.innerWidth;
				this.measureHeroPreviewSlider();
				this.clampHeroPreviewPageIndex();
				this.measureBundleSlider();
				this.clampBundleSliderStartIndex();
			},
			setupHeroPreviewObserver() {
				this.teardownHeroPreviewObserver();

				if (typeof ResizeObserver === 'undefined') {
					return;
				}

				const viewport = this.$refs.refHeroPreviewViewport;
				if (!viewport) {
					return;
				}

				this.heroPreviewResizeObserver = new ResizeObserver(() => {
					this.measureHeroPreviewSlider();
					this.clampHeroPreviewPageIndex();
				});
				this.heroPreviewResizeObserver.observe(viewport);
			},
			teardownHeroPreviewObserver() {
				if (this.heroPreviewResizeObserver) {
					this.heroPreviewResizeObserver.disconnect();
					this.heroPreviewResizeObserver = null;
				}
			},
			measureHeroPreviewSlider() {
				const viewport = this.$refs.refHeroPreviewViewport;
				if (!viewport) {
					this.heroPreviewViewportWidth = 0;
					return;
				}

				this.heroPreviewViewportWidth = Math.max(0, Math.floor(viewport.clientWidth));
			},
			clampHeroPreviewPageIndex() {
				if (this.heroPreviewPageIndex < 0) {
					this.heroPreviewPageIndex = 0;
				}

				if (this.heroPreviewPageIndex > this.maxHeroPreviewPageIndex) {
					this.heroPreviewPageIndex = this.maxHeroPreviewPageIndex;
				}
			},
			slideHeroPreviewPrev() {
				if (!this.canSlideHeroPreviewPrev) {
					return;
				}

				this.heroPreviewPageIndex = Math.max(0, this.heroPreviewPageIndex - 1);
			},
			slideHeroPreviewNext() {
				if (!this.canSlideHeroPreviewNext) {
					return;
				}

				const targetPageIndex = this.heroPreviewPageIndex + 1;
				this.heroPreviewPageIndex = Math.min(targetPageIndex, this.maxHeroPreviewPageIndex);
			},
			setupBundleSliderObserver() {
				this.teardownBundleSliderObserver();

				if (typeof ResizeObserver === 'undefined') {
					return;
				}

				const viewport = this.$refs.refBundleSliderViewport;
				if (!viewport) {
					return;
				}

				this.bundleSliderResizeObserver = new ResizeObserver(() => {
					this.measureBundleSlider();
					this.clampBundleSliderStartIndex();
				});
				this.bundleSliderResizeObserver.observe(viewport);
			},
			teardownBundleSliderObserver() {
				if (this.bundleSliderResizeObserver) {
					this.bundleSliderResizeObserver.disconnect();
					this.bundleSliderResizeObserver = null;
				}
			},
			measureBundleSlider() {
				const viewport = this.$refs.refBundleSliderViewport;
				if (!viewport) {
					this.bundleSliderViewportWidth = 0;
					return;
				}

				const styles = window.getComputedStyle(viewport);
				const paddingLeft = parseFloat(styles.paddingLeft ?? '0') || 0;
				const paddingRight = parseFloat(styles.paddingRight ?? '0') || 0;

				this.bundleSliderViewportWidth = Math.max(0, viewport.clientWidth - paddingLeft - paddingRight);
			},
			clampBundleSliderStartIndex() {
				if (this.bundleSliderStartIndex < 0) {
					this.bundleSliderStartIndex = 0;
				}

				if (this.bundleSliderStartIndex > this.maxBundleSliderStartIndex) {
					this.bundleSliderStartIndex = this.maxBundleSliderStartIndex;
				}
			},
			slideBundlesPrev() {
				if (!this.canSlideBundlesPrev) {
					return;
				}

				this.bundleSliderStartIndex = Math.max(0, this.bundleSliderStartIndex - this.visibleCardsPerView);
			},
			slideBundlesNext() {
				if (!this.canSlideBundlesNext) {
					return;
				}

				const targetIndex = this.bundleSliderStartIndex + this.visibleCardsPerView;
				this.bundleSliderStartIndex = Math.min(targetIndex, this.maxBundleSliderStartIndex);
			},
			bundleVisibleImages(bundle) {
				const collageImages = bundle?.collageImages ?? [];

				if (collageImages.length > 0) {
					return collageImages.slice(0, 3);
				}

				return (bundle?.previewImage != null)
					? [bundle.previewImage].slice(0, 3)
					: [];
			},
			bundleCollageCount(bundle) {
				return Math.max(1, Math.min(this.bundleVisibleImages(bundle).length, 3));
			},
			bundleCollageClass(bundle) {
				return `bundle-collage--count-${this.bundleCollageCount(bundle)}`;
			},
			bundleCollageTileClass(index) {
				return `bundle-collage-tile--index-${index}`;
			},
			showBundleCollageOverflow(bundle, index) {
				const visibleImages = this.bundleVisibleImages(bundle);
				return (bundle?.nrImages ?? visibleImages.length) > visibleImages.length
					&& index === (visibleImages.length - 1);
			},
			bundleCollageOverflowCount(bundle) {
				const visibleImages = this.bundleVisibleImages(bundle);
				return Math.max(0, (bundle?.nrImages ?? visibleImages.length) - visibleImages.length);
			},
			guestClicksPurchase() {
				this.guestClickedPurchase = true;
			},
			preparePaymentForm() {
				if (this.isLoggedIn) this.paymentFormRequested = true;
			},
			prepareSignInForm() {
				this.signInFormRequested = true;
			},
			paymentStarted() {
			},
			paymentStopped() {
			},
			paymentCompleted() {
				this.applySuccessfulPurchase();
				this.hidePaymentOptionsModal();
			},
			hidePaymentOptionsModal() {
				var popup = document.querySelector('#modal-container-payment-form');
				const existingModalInstance = Modal.getInstance(popup);

				if (existingModalInstance) {
					existingModalInstance.hide();
				}
			},
			applySuccessfulPurchase() {
				this.directPurchaseSuccess = true;
				this.guestClickedPurchase = false;
				this.oPurchaseState.ownedBundleIDs = [...this.includedBundleIDs];
				this.oPurchaseState.ungrantedBundleIDs = [...this.includedBundleIDs];
				this.oPurchaseState.grantedBundleIDs = [];
			},
			spendAccountCreditOnSuperBundle() {
				return this.mixinAxios().post('/api/user/super-bundles/SpendAccountCreditOnSuperBundle', this.oSuperBundle.id, {
					headers: {
						'Content-Type': 'application/json'
					}
				})
					.then((result) => {
						var vals = result.data?.vals ?? {};
						var data = result.data ?? {};

						this.oCreditSpend = data.data ?? {};
						this.userNrCredits = vals.nrCredits ?? this.userNrCredits;

						var paymentCompleted = this.oCreditSpend?.success ?? false;
						if (data.success && paymentCompleted) {
							this.applySuccessfulPurchase();
						}
					});
			},
			isIncludedBundleOwned(bundle) {
				return this.ownedBundleIDs.includes(bundle?.id ?? 0);
			},
			bundleLibraryUrl(bundle) {
				return `/my-bundles/${bundle?.id ?? 0}/`;
			},
			bundleDetailUrl(bundle) {
				return bundle?.url ?? '#';
			},
			bundleContextUrl(bundle) {
				return this.isIncludedBundleOwned(bundle)
					? this.bundleLibraryUrl(bundle)
					: this.bundleDetailUrl(bundle);
			},
			bundleContextTarget(bundle) {
				return this.isIncludedBundleOwned(bundle)
					? null
					: '_blank';
			},
			bundleContextRel(bundle) {
				return this.isIncludedBundleOwned(bundle)
					? null
					: 'noopener noreferrer';
			},
			bundleContextLinkText(bundle) {
				return this.isIncludedBundleOwned(bundle)
					? 'View in My Bundles'
					: 'View Bundle';
			},
			async triggerCelebration() {
				var element = this.$refs.refImgSuperBundlePurchased;
				if (!element) return;
				try {
					await ensurePartySdk();
					if (element.isConnected) window.party.confetti(element, { count: window.party.variation.range(100, 100) });
				} catch {
					// A celebration download must not interrupt a completed purchase.
				}
			}
		},
		computed: {
			isLoggedIn() {
				return this.oVisitorStatus?.isLoggedIn ?? false;
			},
			isNotLoggedIn() {
				return !this.isLoggedIn;
			},
			isAdmin() {
				return this.oVisitorStatus?.isAdmin ?? false;
			},
			googleAutoPrompt() {
				return this.showMessageLoginRegister;
			},
			adminEditUrl() {
				return `/admin/super-bundle/${this.oSuperBundle?.id ?? 0}/`;
			},
			orderedIncludedBundles() {
				return [...(this.oSuperBundle?.includedBundles ?? [])];
			},
			includedBundles() {
				return this.orderedIncludedBundles
					.map((bundle, index) => ({ bundle, index }))
					.sort((a, b) => {
						const aOwned = this.isIncludedBundleOwned(a.bundle);
						const bOwned = this.isIncludedBundleOwned(b.bundle);

						if (aOwned !== bOwned) {
							return aOwned ? 1 : -1;
						}

						return a.index - b.index;
					})
					.map(x => x.bundle);
			},
			includedBundleIDs() {
				return this.oPurchaseState?.includedBundleIDs ?? [];
			},
			ownedBundleIDs() {
				return this.oPurchaseState?.ownedBundleIDs ?? [];
			},
			nrIncludedBundles() {
				return this.includedBundles.length;
			},
			nrTotalTemplates() {
				return this.oSuperBundle?.nrTemplates ?? 0;
			},
			nrOwnedBundles() {
				return this.includedBundleIDs.filter((idBundle) => this.ownedBundleIDs.includes(idBundle)).length;
			},
			nrMissingBundles() {
				return Math.max(0, this.nrIncludedBundles - this.nrOwnedBundles);
			},
			ownsEntireSuperBundle() {
				return (this.nrIncludedBundles > 0) && (this.nrOwnedBundles == this.nrIncludedBundles);
			},
			mayPurchase() {
				return this.nrMissingBundles > 0;
			},
			isFree() {
				return (this.oSuperBundle?.nrCredits ?? 0) <= 0;
			},
			isPaid() {
				return !this.isFree;
			},
			showButtonGuest() {
				return this.isNotLoggedIn && this.mayPurchase && !this.guestClickedPurchase && !this.requestActive;
			},
			showButtonClaimOrBuy() {
				return this.isLoggedIn && this.mayPurchase && !this.requestActive;
			},
			showButtonClaim() {
				return this.isFree && this.showButtonClaimOrBuy;
			},
			showButtonBuy() {
				return this.isPaid && this.showButtonClaimOrBuy;
			},
			showButtonProcessing() {
				return this.isLoggedIn && this.mayPurchase && this.requestActive;
			},
			showMessageLoginRegister() {
				return this.isNotLoggedIn && this.guestClickedPurchase && this.mayPurchase;
			},
			showMessageSuperBundlePurchased() {
				return this.directPurchaseSuccess;
			},
			showMessageSuperBundleOwned() {
				return !this.showMessageSuperBundlePurchased && this.ownsEntireSuperBundle;
			},
			showMessageSuperBundlePurchasedOrOwned() {
				return this.showMessageSuperBundlePurchased || this.showMessageSuperBundleOwned;
			},
			showPaymentForm() {
				return this.isLoggedIn && this.paymentFormRequested && this.mayPurchase;
			},
			purchaseButtonText() {
				return (this.isFree)
					? 'Claim free super bundle'
					: `Buy for $${this.formattedCredits}`;
			},
			formattedCredits() {
				return this.formatCredits(this.oSuperBundle?.nrCredits);
			},
			formattedCreditsUndiscounted() {
				return this.formatCredits(this.oSuperBundle?.nrCreditsUndiscounted);
			},
			formattedSavings() {
				return this.formatCredits(this.oSuperBundle?.savings);
			},
			showRawPrice() {
				return this.isPaid && ((this.oSuperBundle?.nrCreditsUndiscounted ?? 0) > 0);
			},
			showSavings() {
				return (this.oSuperBundle?.savings ?? 0) > 0;
			},
			purchaseSummaryText() {
				if (!this.mayPurchase || this.showMessageSuperBundlePurchasedOrOwned) {
					return '';
				}

				if (this.isLoggedIn && this.nrOwnedBundles > 0) {
					return (this.nrMissingBundles == 1)
						? `You already own ${this.nrOwnedBundles} of ${this.nrIncludedBundles} bundles. This purchase adds the remaining 1 bundle to your collection.`
						: `You already own ${this.nrOwnedBundles} of ${this.nrIncludedBundles} bundles. This purchase adds the remaining ${this.nrMissingBundles} to your collection.`;
				}

				if (this.isLoggedIn) {
					return (this.nrIncludedBundles == 1)
						? 'This purchase adds 1 bundle to your collection.'
						: `This purchase adds all ${this.nrIncludedBundles} bundles to your collection.`;
				}

				return (this.nrIncludedBundles == 1)
					? 'Buy once and add 1 bundle to your collection.'
					: `Buy once and add all ${this.nrIncludedBundles} bundles to your collection.`;
			},
			showPurchaseSummary() {
				return isNotNullOrWhiteSpace(this.purchaseSummaryText);
			},
			purchaseInventoryText() {
				const bundleText = (this.nrIncludedBundles == 1)
					? '1 bundle'
					: `${this.nrIncludedBundles.toLocaleString()} bundles`;
				const templateText = (this.nrTotalTemplates == 1)
					? '1 template'
					: `${this.nrTotalTemplates.toLocaleString()} templates`;

				return `${bundleText} ${String.fromCharCode(8226)} ${templateText}`;
			},
			missingIncludedBundles() {
				return this.includedBundles.filter((bundle) => !this.isIncludedBundleOwned(bundle));
			},
			purchaseGrantedBundlesTitle() {
				return (this.missingIncludedBundles.length == 1)
					? 'You will receive this bundle:'
					: 'You will receive these bundles:';
			},
			showGrantedBundlesPill() {
				return this.mayPurchase && (this.missingIncludedBundles.length > 0);
			},
			platformBreakdownEntries() {
				const bundles = this.oSuperBundle?.includedBundles ?? [];
				const counts = {};

				for (const bundle of bundles) {
					const socialMediaTypeName = bundle?.socialMediaTypeName?.trim?.() ?? '';
					if (!socialMediaTypeName) {
						continue;
					}

					counts[socialMediaTypeName] = (counts[socialMediaTypeName] ?? 0) + 1;
				}

				return Object.entries(counts)
					.sort((a, b) => {
						if (b[1] !== a[1]) {
							return b[1] - a[1];
						}

						return a[0].localeCompare(b[0]);
					})
					.map(([name, count]) => `${count} ${name}`);
			},
			platformBreakdownText() {
				return this.platformBreakdownEntries.join(' â€¢ ');
			},
			showPlatformBreakdown() {
				return this.platformBreakdownEntries.length > 0;
			},
			platformBreakdownTextResolved() {
				return this.platformBreakdownEntries.join(` ${String.fromCharCode(8226)} `);
			},
			platformBreakdownTextDisplay() {
				return this.platformBreakdownEntries.join(' • ');
			},
			platformBreakdownTextClean() {
				return this.platformBreakdownEntries.join(' • ');
			},
			showPurchaseOfferSpecs() {
				return this.nrIncludedBundles > 0;
			},
			showDescription() {
				return isNotNullOrWhiteSpace(this.oSuperBundle?.descriptionHtml) ?? false;
			},
			heroPreviewImages() {
				const images = [];

				for (const bundle of this.orderedIncludedBundles) {
					const collageImages = (bundle?.collageImages ?? [])
						.filter((image) => isNotNullOrWhiteSpace(image?.url));
					const previewImages = (collageImages.length > 0)
						? collageImages
						: ((bundle?.previewImage?.url) ? [bundle.previewImage] : []);

					for (let index = 0; index < previewImages.length; index++) {
						const image = previewImages[index];
						images.push({
							key: `hero-preview-${bundle.id}-${index}`,
							url: image.url,
							width: image.width,
							height: image.height,
							srcSet: image.srcSet,
							alt: bundle.name
						});
					}
				}

				return images;
			},
			heroPreviewCardsPerPage() {
				return this.isDesktopLayout
					? 2
					: 1;
			},
			heroPreviewPages() {
				if (this.heroPreviewImages.length === 0) {
					return [];
				}

				const pages = [];
				for (let i = 0; i < this.heroPreviewImages.length; i += this.heroPreviewCardsPerPage) {
					const page = this.heroPreviewImages.slice(i, i + this.heroPreviewCardsPerPage);

					if (this.isDesktopLayout && page.length < this.heroPreviewCardsPerPage) {
						const nrPlaceholderCards = this.heroPreviewCardsPerPage - page.length;
						for (let placeholderIndex = 0; placeholderIndex < nrPlaceholderCards; placeholderIndex++) {
							page.push({
								key: `hero-preview-placeholder-${i}-${placeholderIndex}`,
								isPlaceholder: true
							});
						}
					}

					pages.push(page);
				}

				return pages;
			},
			isDesktopLayout() {
				return this.windowWidth >= 1024;
			},
			heroPreviewImageSizes() {
				return '(min-width: 1488px) 475px, (min-width: 1024px) calc((100vw - 520px) / 2), calc(100vw - 80px)';
			},
			showHeroPreviewSlider() {
				return this.heroPreviewPages.length > 0;
			},
			maxHeroPreviewPageIndex() {
				return Math.max(0, this.heroPreviewPages.length - 1);
			},
			canSlideHeroPreviewPrev() {
				return this.heroPreviewPageIndex > 0;
			},
			canSlideHeroPreviewNext() {
				return this.heroPreviewPageIndex < this.maxHeroPreviewPageIndex;
			},
			heroPreviewPageStyle() {
				if (this.heroPreviewViewportWidth <= 0) {
					return {};
				}

				const width = `${this.heroPreviewViewportWidth}px`;
				return {
					width,
					minWidth: width
				};
			},
			heroPreviewSliderOffset() {
				if (this.heroPreviewViewportWidth <= 0) {
					return 0;
				}

				return this.heroPreviewPageIndex * this.heroPreviewViewportWidth;
			},
			heroPreviewTrackStyle() {
				return {
					transform: `translateX(-${this.heroPreviewSliderOffset}px)`
				};
			},
			visibleCardsPerView() {
				if (this.windowWidth >= 1360) {
					return 3;
				}

				if (this.windowWidth >= 1024) {
					return 2;
				}

				return 1;
			},
			bundleSliderGap() {
				if (this.windowWidth >= 1024) {
					return 24;
				}

				if (this.windowWidth >= 680) {
					return 18;
				}

				return 16;
			},
			maxBundleSliderStartIndex() {
				return Math.max(0, this.includedBundles.length - this.visibleCardsPerView);
			},
			canSlideBundlesPrev() {
				return this.bundleSliderStartIndex > 0;
			},
			canSlideBundlesNext() {
				return this.bundleSliderStartIndex < this.maxBundleSliderStartIndex;
			},
			bundleSlideWidth() {
				if (this.bundleSliderViewportWidth <= 0) {
					return 0;
				}

				return (this.bundleSliderViewportWidth - (this.bundleSliderGap * (this.visibleCardsPerView - 1))) / this.visibleCardsPerView;
			},
			bundleSlideStyle() {
				if (this.bundleSlideWidth <= 0) {
					return {};
				}

				const width = `${this.bundleSlideWidth}px`;
				return {
					width,
					minWidth: width
				};
			},
			bundleSliderOffset() {
				if (this.bundleSlideWidth <= 0) {
					return 0;
				}

				return this.bundleSliderStartIndex * (this.bundleSlideWidth + this.bundleSliderGap);
			},
			bundleSliderTrackStyle() {
				return {
					gap: `${this.bundleSliderGap}px`,
					transform: `translateX(-${this.bundleSliderOffset}px)`
				};
			}
		},
		watch: {
			showMessageSuperBundlePurchased(newVal) {
				if (newVal) {
					this.$nextTick(() => {
						this.triggerCelebration();
					});
				}
			},
			heroPreviewImages() {
				this.$nextTick(() => {
					this.measureHeroPreviewSlider();
					this.clampHeroPreviewPageIndex();
					this.setupHeroPreviewObserver();
				});
			},
			heroPreviewCardsPerPage() {
				this.$nextTick(() => {
					this.measureHeroPreviewSlider();
					this.clampHeroPreviewPageIndex();
				});
			},
			nrIncludedBundles() {
				this.$nextTick(() => {
					this.measureBundleSlider();
					this.clampBundleSliderStartIndex();
				});
			},
			visibleCardsPerView() {
				this.$nextTick(() => {
					this.measureBundleSlider();
					this.clampBundleSliderStartIndex();
				});
			}
		}
	};
</script>




<style scoped>@import '../scss/vue/ArticleHtml.scss'</style>
<style scoped>
	[v-cloak] {
		display: none;
	}

	#super-bundle-view {
		padding: 24px 16px 56px 16px;
	}

	.page-shell {
		max-width: 1440px;
		margin: 0 auto;
	}

	.hero-card,
	.content-card,
	.purchase-card {
		border: 1px solid rgba(148, 163, 184, 0.22);
		border-radius: 30px;
		background:
			radial-gradient(circle at top right, rgba(191, 219, 254, 0.18), transparent 28%),
			#ffffff;
		box-shadow: 0px 26px 48px -42px rgba(15, 23, 42, 0.65);
	}

	.top-grid {
		display: grid;
		gap: 24px;
		align-items: stretch;
		grid-template-columns: minmax(0, 1fr);
	}

	.hero-card,
	.content-card,
	.purchase-card {
		padding: 24px;
	}

	.hero-title-row {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 16px;
	}

	.hero-card {
		display: flex;
		flex-direction: column;
		height: 100%;
		min-width: 0;
	}

	.hero-title,
	.section-title {
		margin: 0;
		color: #172554;
	}

	.hero-title {
		font-size: clamp(2.3rem, 5vw, 4rem);
		line-height: 0.98;
	}

	.hero-edit-link {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-height: 56px;
		padding: 0px 22px;
		border: 1px solid rgba(15, 118, 110, 0.18);
		border-radius: 999px;
		background: rgba(255, 255, 255, 0.92);
		font-size: clamp(1.05rem, 1.8vw, 1.35rem);
		font-weight: 800;
		color: #0f766e;
		text-decoration: none;
		box-shadow: 0px 12px 24px -22px rgba(15, 23, 42, 0.65);
	}

	.hero-description {
		margin: 0;
		color: #334155;
	}

	.hero-description :deep(p:first-child) {
		margin-top: 0;
	}

	.hero-body {
		display: flex;
		flex: 1;
		flex-direction: column;
		min-height: 0;
		min-width: 0;
	}

	.hero-preview-shell {
		margin-top: 28px;
		min-width: 0;
	}

	.hero-preview-slider {
		position: relative;
		min-width: 0;
	}

	.hero-preview-viewport {
		overflow: hidden;
		min-width: 0;
	}

	.hero-preview-track {
		display: flex;
		align-items: stretch;
		transition: transform 200ms ease-in-out;
		will-change: transform;
	}

	.hero-preview-page {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 18px;
		align-items: stretch;
	}

	.hero-preview-card {
		overflow: hidden;
		border-radius: 24px;
		border: 1px solid rgba(191, 219, 254, 0.58);
		background: linear-gradient(135deg, rgba(239, 246, 255, 0.9), rgba(248, 250, 252, 0.96));
		box-shadow: 0px 22px 38px -30px rgba(15, 23, 42, 0.32);
		aspect-ratio: 3 / 2;
	}

	.hero-preview-card img {
		box-sizing: border-box;
		display: block;
		width: 100%;
		height: 100%;
		padding: 14px;
		object-fit: contain;
		background: transparent;
	}

	.hero-preview-card--placeholder {
		border-style: dashed;
		border-color: var(--cb-placeholder-accent);
		background: linear-gradient(135deg, var(--cb-placeholder-fill), var(--cb-placeholder-surface));
	}

	.hero-preview-placeholder {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
		padding: 20px;
		text-align: center;
		color: var(--cb-placeholder-text);
	}

	.hero-preview-placeholder-mark {
		font-size: clamp(1rem, 2vw, 1.2rem);
		font-weight: 900;
		letter-spacing: 0.04em;
		text-transform: uppercase;
	}

	.hero-preview-placeholder-copy {
		margin-top: 10px;
		font-size: 0.95rem;
		font-weight: 700;
		line-height: 1.5;
		color: var(--cb-placeholder-text);
	}

	.hero-preview-arrow {
		appearance: none;
		-webkit-appearance: none;
		position: absolute;
		top: 50%;
		z-index: 12;
		width: 42px;
		height: 42px;
		border: 1px solid rgb(67, 56, 202);
		border-radius: 999px;
		background: rgb(67, 56, 202);
		background-image: none;
		box-shadow: 0px 18px 30px -22px rgba(67, 56, 202, 0.45);
		font-size: 1rem;
		font-weight: 900;
		line-height: 1;
		color: #ffffff;
		transform: translateY(-50%);
	}

	.hero-preview-arrow--left {
		left: -12px;
	}

	.hero-preview-arrow--right {
		right: -12px;
	}

	.hero-preview-arrow:disabled {
		cursor: not-allowed;
		opacity: 1;
		border-color: rgba(67, 56, 202, 0.45);
		background: rgba(67, 56, 202, 0.45);
		background-image: none;
		color: rgba(255, 255, 255, 0.92);
		box-shadow: none;
	}

	.hero-description-shell {
		margin-top: 26px;
	}

	.hero-description-shell--after-preview {
		padding-top: 22px;
		border-top: 1px solid rgba(191, 219, 254, 0.9);
	}

	.purchase-column {
		align-self: start;
	}

	.purchase-card {
		position: relative;
		overflow: visible;
	}

	.purchase-top-actions {
		display: grid;
		gap: 0;
	}

	.purchase-price-block {
		display: flex;
		align-items: baseline;
		justify-content: space-between;
		gap: 18px;
		margin-top: 16px;
	}

	.purchase-price-raw {
		margin: 0;
		font-size: 1.1rem;
		font-weight: 700;
		color: #dc2626;
		text-decoration: line-through;
		text-decoration-thickness: 2px;
	}

	.purchase-savings-row {
		margin: 0;
		font-size: 1rem;
		font-weight: 800;
		color: #1d4ed8;
		text-align: right;
	}

	.purchase-summary-copy {
		margin: 16px 0px 0px 0px;
		padding: 16px 18px;
		border-radius: 22px;
		background: rgba(224, 231, 255, 0.56);
		font-size: 0.98rem;
		font-weight: 600;
		line-height: 1.6;
		color: #1e3a8a;
	}

	.purchase-offer-specs {
		margin-top: 16px;
		padding: 14px 16px;
		border: 1px solid rgba(191, 219, 254, 0.9);
		border-radius: 20px;
		background: rgba(248, 250, 252, 0.92);
	}

	.purchase-offer-line {
		margin: 0;
		font-size: 0.98rem;
		font-weight: 800;
		line-height: 1.5;
		color: #172554;
	}

	.purchase-offer-line--secondary {
		margin-top: 6px;
		font-size: 0.94rem;
		font-weight: 700;
		color: #475569;
	}

	.purchase-button-container {
		margin-top: 0;
	}

	.purchase-button {
		width: 100%;
		min-height: 54px;
		font-size: 1.08rem;
		font-weight: 800;
		border-radius: 999px;
	}

	.purchase-login-box,
	.purchase-owned-box {
		margin-top: 18px;
		padding: 18px;
		border-radius: 22px;
	}

	.purchase-granted-pill {
		margin-top: 14px;
		padding: 16px 18px;
		border-radius: 22px;
		background: rgba(220, 252, 231, 0.88);
		color: #166534;
	}

	.purchase-granted-title {
		margin: 0;
		font-size: 0.92rem;
		font-weight: 800;
		line-height: 1.4;
		color: #166534;
	}

	.purchase-granted-list {
		margin: 10px 0px 0px 0px;
		padding-left: 22px;
	}

	.purchase-granted-list li + li {
		margin-top: 8px;
	}

	.purchase-granted-list a {
		font-size: 0.95rem;
		font-weight: 700;
		line-height: 1.55;
		color: #166534;
		text-decoration: underline;
	}

	.purchase-login-box {
		border: 1px solid rgba(148, 163, 184, 0.3);
		background: rgba(248, 250, 252, 0.92);
		text-align: center;
	}

	.purchase-owned-box {
		background: rgba(220, 252, 231, 0.88);
		text-align: center;
		color: #166534;
	}

	.purchase-owned-box img {
		width: 64px;
		height: 64px;
	}

	.purchase-owned-title {
		margin: 10px 0px 0px 0px;
		font-size: 1.08rem;
		font-weight: 800;
	}

	.purchase-owned-copy {
		margin: 8px 0px 0px 0px;
		color: inherit;
	}

	.fat-link {
		color: #0f766e;
		font-weight: 800;
		text-decoration: underline;
	}

	.included-section {
		margin-top: 24px;
	}

	.section-title {
		font-size: clamp(1.65rem, 2.8vw, 2.4rem);
	}

	.bundle-slider-shell {
		position: relative;
		margin-top: 22px;
		overflow: visible;
	}

	.bundle-slider-viewport {
		overflow: hidden;
		padding: 14px 16px 0px 0px;
	}

	.bundle-slider-track {
		display: flex;
		align-items: stretch;
		transition: transform 200ms ease-in-out;
		will-change: transform;
	}

	.bundle-slider-arrow {
		appearance: none;
		-webkit-appearance: none;
		position: absolute;
		top: 50%;
		z-index: 12;
		width: 50px;
		height: 50px;
		border: 1px solid rgb(67, 56, 202);
		border-radius: 999px;
		background: rgb(67, 56, 202);
		background-image: none;
		box-shadow: 0px 18px 30px -22px rgba(67, 56, 202, 0.45);
		font-size: 1.2rem;
		font-weight: 900;
		line-height: 1;
		color: #ffffff;
		transform: translateY(-50%);
	}

	.bundle-slider-arrow--left {
		left: -16px;
	}

	.bundle-slider-arrow--right {
		right: -16px;
	}

	.bundle-slider-arrow:disabled {
		cursor: not-allowed;
		opacity: 1;
		border-color: rgba(67, 56, 202, 0.45);
		background: rgba(67, 56, 202, 0.45);
		background-image: none;
		color: rgba(255, 255, 255, 0.92);
		box-shadow: none;
	}

	.bundle-entry-card {
		position: relative;
		display: grid;
		gap: 18px;
		grid-template-rows: auto auto auto;
		padding: 18px;
		border: 1px solid rgba(191, 219, 254, 0.78);
		border-radius: 28px;
		background: #ffffff;
		box-shadow: 0px 20px 38px -34px rgba(15, 23, 42, 0.5);
		overflow: visible;
	}

	.bundle-entry-media,
	.bundle-entry-title a,
	.bundle-entry-action-link {
		text-decoration: none;
	}

	.bundle-collage {
		display: grid;
		width: 100%;
		gap: 10px;
		align-items: start;
	}

	.bundle-collage--count-1 {
		grid-template-columns: 1fr;
		grid-template-rows: 1fr;
	}

	.bundle-collage--count-2 {
		grid-template-columns: 1fr;
		grid-template-rows: repeat(2, minmax(0, 1fr));
	}

	.bundle-collage--count-3 {
		grid-template-columns: 1fr;
		grid-template-rows: repeat(3, minmax(0, 1fr));
	}

	.bundle-collage-tile,
	.bundle-collage-fallback {
		position: relative;
		width: 100%;
		aspect-ratio: 3 / 2;
		overflow: hidden;
		border-radius: 20px;
		background: linear-gradient(135deg, var(--cb-placeholder-fill), var(--cb-placeholder-surface));
	}

	.bundle-collage-tile img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
		background: #ffffff;
	}

	.bundle-collage-fallback {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 18px;
		font-size: 0.95rem;
		font-weight: 700;
		text-align: center;
		color: var(--cb-placeholder-text);
	}

	.bundle-collage-overlay {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 12px;
		background: rgba(15, 23, 42, 0.58);
		font-size: 1rem;
		font-weight: 800;
		text-align: center;
		color: #ffffff;
	}

	.bundle-entry-header {
		min-height: 0;
	}

	.bundle-entry-title {
		margin: 0;
		font-size: 1.24rem;
		line-height: 1.22;
		min-height: 2.44em;
	}

	.bundle-entry-title a {
		color: #172554;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		overflow: hidden;
	}

	.bundle-entry-title a:hover {
		color: #1d4ed8;
	}

	.bundle-entry-action-link {
		display: block;
		width: 100%;
		text-align: center;
		font-weight: 800;
		color: #0f766e;
		text-decoration: underline;
	}

	.included-empty {
		margin-top: 18px;
		padding: 18px;
		border-radius: 22px;
		background: rgba(248, 250, 252, 0.92);
		color: #475569;
	}

	.img-payment-form-loader {
		width: 48px;
		height: 48px;
	}

	.ribbon-wrap {
		position: absolute;
		right: -36px;
		top: 17px;
		width: 130px;
		-ms-transform: rotate(45deg);
		-webkit-transform: rotate(45deg);
		transform: rotate(45deg);
		border-left: 25px solid transparent;
		border-right: 25px solid transparent;
		border-bottom: 25px solid #ff4444;
		box-shadow: 0px 6px 5px -5px rgba(0, 0, 0, 0.5);
		z-index: 14;
	}

	.ribbon-wrap .ribbon-content {
		position: absolute;
		left: 17px;
		top: 3px;
		font-size: 15px;
		font-weight: 800;
		line-height: 19px;
		color: #ffffff;
		width: calc(100% - 34px);
		text-align: center;
		word-spacing: -1px;
	}

	.purchase-ribbon {
		right: -36px;
		top: 17px;
	}

	.included-ribbon {
		right: -36px;
		top: 17px;
	}

	@media all and (min-width: 1024px) {
		#super-bundle-view {
			padding: 32px 24px 72px 24px;
		}

		.top-grid {
			grid-template-columns: minmax(0, 1fr) minmax(360px, 400px);
		}

		.purchase-column {
			position: sticky;
			top: 24px;
		}
	}

	@media all and (max-width: 1023px) {
		.hero-preview-shell {
			margin-top: 22px;
		}

		.hero-preview-page {
			grid-template-columns: minmax(0, 1fr);
			gap: 0;
		}

		.hero-preview-card {
			aspect-ratio: 3 / 2;
		}

		.hero-preview-arrow--left {
			left: -10px;
		}

		.hero-preview-arrow--right {
			right: -10px;
		}

		.bundle-slider-arrow--left {
			left: -8px;
		}

		.bundle-slider-arrow--right {
			right: -8px;
		}
	}

	@media all and (max-width: 679px) {
		#super-bundle-view {
			padding: 18px 12px 44px 12px;
		}

		.hero-card,
		.content-card,
		.purchase-card {
			padding: 18px;
			border-radius: 24px;
		}

		.hero-title-row {
			align-items: start;
		}

		.hero-edit-link {
			min-height: 48px;
			padding: 0px 18px;
			font-size: 1rem;
		}

		.hero-preview-shell {
			margin-top: 18px;
		}

		.hero-description-shell {
			margin-top: 20px;
		}

		.hero-description-shell--after-preview {
			padding-top: 18px;
		}

		.hero-preview-arrow {
			width: 38px;
			height: 38px;
			font-size: 0.95rem;
		}

		.bundle-slider-arrow {
			width: 42px;
			height: 42px;
			font-size: 1rem;
		}

		.bundle-slider-arrow--left {
			left: -4px;
		}

		.bundle-slider-arrow--right {
			right: -4px;
		}

		.bundle-entry-card {
			padding: 16px;
			border-radius: 22px;
		}

		.bundle-collage {
			gap: 8px;
		}

		.ribbon-wrap {
			right: -34px;
			top: 14px;
			width: 126px;
		}

		.ribbon-wrap .ribbon-content {
			left: 16px;
			width: calc(100% - 32px);
			font-size: 14px;
		}
	}
</style>
